import React, { useState, useEffect, useRef } from 'react';
import { Button, FormGroup, Input, Label, Table } from 'reactstrap';
import { CspModal, CspModalBody, CspModalFooter, CspModalHeader } from '../CspModal';
import general_fetch from '../../utils/fetch';
import {
	normalise_gst,
	find_gst_matches,
	derive_match_state,
	build_multi_match_csv,
	is_dump_unavailable_error,
} from './zoho_gst_match';

/**
 * Two-step modal shown when an org has no zoho_contact_id and the CS user asks
 * for an estimated invoice. Step 1 confirms the GST; step 2 shows what that GST
 * resolves to in Zoho and, on a single match, hands the resolved contact id back
 * to the caller so the existing download can proceed.
 *
 * The parent owns the download — this component never calls get_zoho_estimate.
 */
const ZohoDetailsMissingModal = ({ open, toggle, store_info, on_confirm }) => {
	const [step, set_step] = useState(0);
	const [gst, set_gst] = useState('');
	const [matches, set_matches] = useState([]);
	// The GST `matches` was actually resolved from. Step 2 has no GST input today,
	// so this always equals the current GST — pinning it keeps that an enforced
	// invariant rather than a coincidence of the current layout.
	const [matched_gst, set_matched_gst] = useState('');
	const [dump_unavailable, set_dump_unavailable] = useState(false);
	const [loading, set_loading] = useState(false);
	const [show_gst_confirm, set_show_gst_confirm] = useState(false);
	// A ref, not state: the latch must take effect within a single click's
	// handler, before React has re-rendered.
	const confirmed = useRef(false);

	const original_gst = store_info && store_info.gst_number ? store_info.gst_number : '';
	const org_name = store_info && store_info.name ? store_info.name : '';

	// Reset on open so a previous run's matches never leak into a new one.
	// Keyed on `open` alone: store_info is refetched while this modal can still
	// be mounted, and depending on original_gst would wipe what the user typed
	// mid-flow when that refetch resolves.
	useEffect(() => {
		if (!open) return;
		set_step(0);
		set_gst(store_info && store_info.gst_number ? store_info.gst_number : '');
		set_matches([]);
		set_matched_gst('');
		set_dump_unavailable(false);
		set_loading(false);
		set_show_gst_confirm(false);
		confirmed.current = false;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open]);

	// True only when the user actually changed the GST — drives both the
	// confirmation modal and whether gst_number is sent for persistence.
	const gst_edited = normalise_gst(gst) !== normalise_gst(original_gst);
	const match_state = derive_match_state(matches, dump_unavailable);

	const on_continue = async () => {
		if (!normalise_gst(gst)) return;
		set_loading(true);
		try {
			const { url } = await general_fetch({ url: 'csp/get_zoho_contacts_dump_url', body: {} });
			// Deliberately not general_fetch: this is a signed GCS URL on another
			// origin, and general_fetch would attach credentials and a JSON
			// content-type, both of which fail CORS preflight there.
			const resp = await fetch(url);
			if (!resp.ok) throw new Error(`dump fetch failed with status ${resp.status}`);
			const dump = await resp.json();
			set_matches(find_gst_matches(dump, gst));
			set_matched_gst(normalise_gst(gst));
			set_dump_unavailable(false);
		} catch (err) {
			// ZOHO_CONTACTS_DUMP_NOT_AVAILABLE is the expected state until the
			// weekly cron is scheduled; anything else is a genuine failure. Both
			// land the user in the same place — nothing to match against — so
			// they differ only in how loudly they are logged.
			if (is_dump_unavailable_error(err)) {
				console.log('zoho contacts dump has not been generated yet', err);
			} else {
				console.error('could not resolve zoho contact from gst', err);
			}
			set_matches([]);
			set_matched_gst('');
			set_dump_unavailable(true);
		}
		set_loading(false);
		set_step(1);
	};

	const download_candidates_csv = () => {
		// The searched GST, not store_info's — when the user edited it, the
		// candidates were found by the edited value, and an infurnia gst column
		// that disagreed with the zoho gst column would just confuse the admin.
		const csv = build_multi_match_csv(matches, normalise_gst(gst), org_name);
		const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' });
		const url = window.URL.createObjectURL(blob);
		const link = document.createElement('a');
		link.href = url;
		link.download = `zoho_duplicate_contacts_${normalise_gst(gst)}.csv`;
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
		window.URL.revokeObjectURL(url);
	};

	// On a single match the download is allowed. If the GST was edited we ask for
	// confirmation first, because accepting also writes the new GST to the org.
	const on_download = () => {
		if (match_state !== 'single') return;
		// Refuse to download against a contact resolved from a different GST.
		if (matched_gst !== normalise_gst(gst)) { set_step(0); return; }
		if (gst_edited) { set_show_gst_confirm(true); return; }
		confirm_download();
	};

	const confirm_download = () => {
		// toggle() unmounts nothing synchronously, so without this latch a fast
		// double-click fires on_confirm — and therefore get_zoho_estimate — twice.
		if (confirmed.current) return;
		const match = matches[0];
		if (!match) return;
		confirmed.current = true;
		set_show_gst_confirm(false);
		toggle();
		on_confirm({
			zoho_contact_id: match.zoho_contact_id,
			gst_number: gst_edited ? normalise_gst(gst) : undefined,
		});
	};

	const step_2_body = () => {
		if (match_state === 'single') {
			const match = matches[0];
			return (
				<>
					<div className='mb-3'>Confirm the following details before proceeding to estimate generation</div>
					<FormGroup>
						<Label className='font-weight-bold small'>Zoho org name</Label>
						<Input readOnly value={match.zoho_name || ''} />
					</FormGroup>
					<FormGroup>
						<Label className='font-weight-bold small'>Infurnia org name</Label>
						<Input readOnly value={org_name} />
					</FormGroup>
				</>
			);
		}
		if (match_state === 'none') {
			return (
				<div>
					No org found on zoho with the gst number <b>{normalise_gst(gst)}</b>. Either go back
					and update this gst number or contact the Accounts admin for help.
				</div>
			);
		}
		if (match_state === 'multiple') {
			return (
				<>
					<div className='mb-3'>
						Multiple active zoho orgs found with the entered gst number <b>{normalise_gst(gst)}</b>.
						Contact your Accounts admin and request them to make all other zoho orgs with the
						same gst number as inactive.
					</div>
					<Table size='sm' bordered className='mb-3'>
						<thead>
							<tr><th>Zoho contact id</th><th>Zoho name</th></tr>
						</thead>
						<tbody>
							{matches.map(match => (
								<tr key={match.zoho_contact_id}>
									<td>{match.zoho_contact_id}</td>
									<td>{match.zoho_name}</td>
								</tr>
							))}
						</tbody>
					</Table>
					<Button className='btn-sm' color='secondary' onClick={download_candidates_csv}>
						Download CSV of matching zoho orgs
					</Button>
				</>
			);
		}
		return (
			<div>
				Zoho contact details are currently unavailable. Please contact the Accounts admin for help.
			</div>
		);
	};

	return (
		<>
			<CspModal open={open} toggle={toggle} size='md'>
				<CspModalHeader title='Zoho details missing' toggle={toggle} />
				<CspModalBody>
					{step === 0 ? (
						<>
							<div className='mb-3'>
								Zoho details for this org were not found. Please confirm the GST number for this org.
							</div>
							<FormGroup>
								<Label className='font-weight-bold small' for='zoho_modal_gst'>GST number</Label>
								<Input
									id='zoho_modal_gst'
									value={gst}
									onChange={e => set_gst(e.target.value)}
									placeholder='Enter GST number'
								/>
							</FormGroup>
						</>
					) : step_2_body()}
				</CspModalBody>
				<CspModalFooter>
					{step === 0 ? (
						<Button
							className='btn-sm'
							color='primary'
							disabled={loading || !normalise_gst(gst)}
							onClick={on_continue}
						>
							{loading ? 'Please wait...' : 'Continue'}
						</Button>
					) : (
						<>
							<Button className='btn-sm' color='secondary' onClick={() => set_step(0)}>Back</Button>
							<Button
								className='btn-sm'
								color='primary'
								disabled={match_state !== 'single'}
								onClick={on_download}
							>
								Download Estimated Invoice
							</Button>
						</>
					)}
				</CspModalFooter>
			</CspModal>

			<CspModal open={show_gst_confirm} toggle={() => set_show_gst_confirm(false)} size='md'>
				<CspModalHeader title='Confirm GST update' toggle={() => set_show_gst_confirm(false)} />
				<CspModalBody>
					The entered GST number <b>{normalise_gst(gst)}</b> will be updated for the current
					org <b>{org_name}</b>. Are you sure?
				</CspModalBody>
				<CspModalFooter>
					<Button className='btn-sm' color='secondary' onClick={() => set_show_gst_confirm(false)}>Cancel</Button>
					<Button className='btn-sm' color='primary' onClick={confirm_download}>
						Accept and Download invoice
					</Button>
				</CspModalFooter>
			</CspModal>
		</>
	);
};

export default ZohoDetailsMissingModal;
